import React from 'react';

import {
  Button,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { sendAttendContactAbsence } from '../thunk';

import Confirm from './Confirm';

import type { AttendContactAbsenceJson } from '@/types/domain/attendContact/entities/attendContactAbsenceEntity';
import type { AbsenceReasonType } from '@/types/domain/attendContact/valueObjects/absenceReason';
import type { AbsenceReasonDetailInfectionType } from '@/types/domain/attendContact/valueObjects/absenceReasonDetailInfection';
import type { AbsenceReasonDetailSickType } from '@/types/domain/attendContact/valueObjects/absenceReasonDetailSick';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { FacilityJson } from '@/types/domain/childApply/entities/facilityEntity';

import DateFieldComp from '@/components/DateFieldComp';
import SelectFieldComp from '@/components/SelectFieldComp';
import TextFieldComp from '@/components/TextFieldComp';
import { AbsenceDate } from '@/domain/attendContact/service/entity/valueObjects/absenceDate';
import { AbsenceReason } from '@/domain/attendContact/service/entity/valueObjects/absenceReason';
import { AbsenceReasonDetailInfection } from '@/domain/attendContact/service/entity/valueObjects/absenceReasonDetailInfection';
import { AbsenceReasonDetailSick } from '@/domain/attendContact/service/entity/valueObjects/absenceReasonDetailSick';
import { Memo } from '@/domain/attendContact/service/entity/valueObjects/memo';
import { ABSENCE_REASON_DETAIL_INFECTION_TYPE } from '@/types/domain/attendContact/valueObjects/absenceReasonDetailInfection';
import { ABSENCE_REASON_DETAIL_SICK_TYPE } from '@/types/domain/attendContact/valueObjects/absenceReasonDetailSick';

/** プロパティ */
export type Props = {
  /** 子ども */
  child: ChildJson,
  /** 子どもの施設リスト */
  facilityList: FacilityJson[],
};

type AttendContactAbsenceJsonInput = {
  nurseryId: string,
  absenceDate: string,
  absenceReason: AbsenceReasonType,
  absenceReasonDetailSick?: AbsenceReasonDetailSickType,
  absenceReasonDetailInfection?: AbsenceReasonDetailInfectionType,
  memo: string,
};

const Absence: React.FC<Props> = ({ child, facilityList }) => {
  const [
    confirmData,
    setConfirmData,
  ] = React.useState<AttendContactAbsenceJson | undefined>(undefined);
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<AttendContactAbsenceJsonInput>({
    defaultValues: {
      absenceDate: AbsenceDate.toStringDate(new Date()),
      absenceReason: '私事都合',
      absenceReasonDetailSick: ABSENCE_REASON_DETAIL_SICK_TYPE[0],
      absenceReasonDetailInfection: ABSENCE_REASON_DETAIL_INFECTION_TYPE[0],
      memo: '',
      nurseryId: facilityList[0]?.data.facilityId,
    },
  });
  const dispatch = useDispatch();
  const absenceReason = watch('absenceReason');
  const onSubmit = (input: AttendContactAbsenceJsonInput) => {
    setConfirmData({
      data: {
        nurseryId: input.nurseryId,
        parentId: child.parentId,
        childId: child.childId,
        absenceDate: input.absenceDate,
        absenceReason: input.absenceReason,
        absenceReasonDetailSick: input.absenceReason === '病欠' ? input.absenceReasonDetailSick : undefined,
        absenceReasonDetailInfection:
          input.absenceReason === '感染症'
            ? input.absenceReasonDetailInfection
            : undefined,
        memo: input.memo,
      },
      pub: {
        sendId: `${child.parentId}_${child.childId}`,
        replyId: input.nurseryId,
        data: {},
      },
    });
  };
  let title = '';
  const contents = [];
  if (facilityList.length > 0) {
    contents.push(
      <SelectFieldComp
        key="nurseryId"
        control={control}
        name="nurseryId"
        label="送信先"
        rules={{
          required: '必須項目です',
        }}
        errors={errors}
        items={facilityList.map((facility) => ({
          id: facility.data.facilityId,
          name: facility.data.facilityName,
        }))}
        disabled={facilityList.length <= 1}
      />,
    );
  }
  contents.push(
    <DateFieldComp
      key="absenceDate"
      control={control}
      name="absenceDate"
      label={AbsenceDate.label}
      rules={AbsenceDate.validationRule}
      errors={errors}
    />,
  );
  title = '欠席連絡';
  contents.push(
    <SelectFieldComp
      key="absenceReason"
      control={control}
      name="absenceReason"
      label={AbsenceReason.label}
      rules={AbsenceReason.validationRule}
      errors={errors}
      items={AbsenceReason.items}
    />,
  );
  if (absenceReason === '病欠') {
    contents.push(
      <SelectFieldComp
        key="absenceReasonDetailSick"
        control={control}
        name="absenceReasonDetailSick"
        label={AbsenceReasonDetailSick.label}
        rules={AbsenceReasonDetailSick.validationRule}
        errors={errors}
        items={AbsenceReasonDetailSick.items}
      />,
    );
  } else if (absenceReason === '感染症') {
    contents.push(
      <SelectFieldComp
        key="absenceReasonDetailInfection"
        control={control}
        name="absenceReasonDetailInfection"
        label={AbsenceReasonDetailInfection.label}
        rules={AbsenceReasonDetailInfection.validationRule}
        errors={errors}
        items={AbsenceReasonDetailInfection.items}
      />,
    );
  }
  contents.push(
    <TextFieldComp
      key="memo"
      type="text"
      control={control}
      rules={Memo.validationRule}
      name="memo"
      label={Memo.label}
      placeholder={Memo.placeholder}
      errors={errors}
      multiline
    />,
  );
  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <p>{title}</p>
        <div style={{ margin: '10px 0' }}>
          {contents}
        </div>
        <Button
          variant="contained"
          type="submit"
          color="primary"
          fullWidth
        >
          確認
        </Button>
      </form>
      <Confirm
        data={confirmData}
        onSend={(item) => {
          if (item) {
            dispatch(sendAttendContactAbsence(item));
          }
          setConfirmData(undefined);
        }}
      />
    </div>
  );
};

export default Absence;
